import React, { useState, useEffect } from "react";
import { DollarSign, ShoppingCart, Zap, TrendingUp } from "lucide-react";
import { toast } from "react-toastify";
import { getDashboardMetrics } from "../../../../services/apiAndBillingService";

const MetricsCards = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const data = await getDashboardMetrics();
        setMetrics(data);
      } catch (err) {
        toast.error(`Failed to load metrics: ${err.message}`);
        console.error('Error fetching dashboard metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const formatValue = (value, type) => {
    if (type === 'currency') {
      return `$${value.toLocaleString()}`;
    } else if (type === 'number') {
      return value.toLocaleString();
    } else if (type === 'largeNumber') {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
      return value.toLocaleString();
    }
    return value;
  };

  const getChangeColor = (change) => {
    return change >= 0 ? "text-green-600" : "text-red-600";
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 animate-pulse">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 sm:mb-8">
        <p className="text-red-600 text-sm">Error loading metrics: {error}</p>
        <button 
          onClick={() => {
            setError(null);
            setLoading(true);
            // Trigger refetch by calling the effect again
            window.location.reload();
          }}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  const metricsData = [
    {
      title: "Monthly Revenue",
      value: formatValue(metrics.monthlyRevenue.value, 'currency'),
      change: metrics.monthlyRevenue.changeLabel,
      icon: DollarSign,
      iconBg: "bg-green-100",
      iconColor: "text-green-600"
    },
    {
      title: "Active Purchases",
      value: formatValue(metrics.activePurchases.value, 'number'),
      change: metrics.activePurchases.changeLabel,
      icon: ShoppingCart,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "API Requests",
      value: formatValue(metrics.apiRequests.value, 'largeNumber'),
      change: metrics.apiRequests.changeLabel,
      icon: Zap,
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600"
    },
    {
      title: "Avg Revenue Per User",
      value: formatValue(metrics.avgRevenuePerUser.value, 'currency'),
      change: metrics.avgRevenuePerUser.changeLabel,
      icon: TrendingUp,
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600"
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
      {metricsData.map((metric, index) => (
        <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">{metric.title}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{metric.value}</p>
              <p className={`text-xs sm:text-sm font-medium ${getChangeColor(metric.change)}`}>
                {metric.change}
              </p>
            </div>
            <div className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${metric.iconBg}`}>
              <metric.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${metric.iconColor}`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MetricsCards;
